import React, { useEffect, useState} from 'react';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { redirectToCheckout } from '../../pages/app/protected/services/BillingPayment';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db } from '../../pages/app/protected/firebase';
import { quotas } from '../../pages/app/protected/services/QuotasConfig';
import { useSearchParams } from 'react-router-dom';

export default function BillingPage() {
  const [companyId, setCompanyId] = useState('');
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('subscription');
  const [subscription, setSubscription] = useState(null);
  const [createdAt, setCreatedAt] = useState('');
  const [billingCycle, setBillingCycle] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [usedQuotas, setUsedQuotas] = useState({
    productTypes: 0,
    products: 0,
    discounts: 0,
    cashiers: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoadingPage(true);
      if (!user || sessionId) {
        setLoadingPage(false);
        return;
      }
  
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists() || !userDoc.data().companyId) {
          throw new Error('No company associated with this user');
        }
        const currentCompanyId = userDoc.data().companyId;
        setCompanyId(currentCompanyId);

        const companyRef = doc(db, 'companies', currentCompanyId);
        const companySnap = await getDoc(companyRef);
        
        if (companySnap.exists()) {
          const data = companySnap.data();
          setSubscription(data.subscription || {});
          setCreatedAt(data.createdAt || '');
          setBillingCycle(data.subscription?.billingCycle || 'No Billing Cycle');
  
          const customerId = data.subscription?.customerId;
  
          if (customerId) {
            setLoadingInvoices(true);
            const functions = getFunctions(app);
            const getInvoices = httpsCallable(functions, 'getInvoices');
            getInvoices({ companyId: currentCompanyId })
              .then(({ data: stripeInvoices }) => setInvoices(stripeInvoices))
              .catch((err) => console.error("Failed to load invoices:", err))
              .finally(() => setLoadingInvoices(false));
          } else {
            setInvoices([]);
            setLoadingInvoices(false);
          }
        }

        // Load counts and quotas after basic data is loaded
        await loadCounts(currentCompanyId);
        
        // Refresh subscription data in the background to avoid blocking render
        checkAndRefreshSubscription(currentCompanyId).catch(() => {});
        
      } catch (err) {
        console.error("Error fetching billing data:", err);
      } finally {
        setLoadingPage(false);
      }
    };

    const checkAndRefreshSubscription = async (companyId) => {
      try {
        const functions = getFunctions(app);
        const result = await httpsCallable(functions, 'getLatestSubscription')({ companyId });
        return result;
      } catch (err) {
        console.error("Failed to refresh subscription:", err.message);
        throw err;
      }
    };

    const fetchCount = async (companyId, path) => {
      if (!companyId) return 0;
      const snap = await getDocs(collection(db, ...path));
      return snap.size;
    };

    const loadCounts = async (companyId) => {
      if (!companyId) return;

      const [counts, mainDocSnap] = await Promise.all([
        Promise.all([
          fetchCount(companyId, ['companies', companyId, 'companySettings', 'inventory', 'productTypes']),
          fetchCount(companyId, ['companies', companyId, 'inventory', 'data', 'products']),
          fetchCount(companyId, ['companies', companyId, 'companySettings', 'discounts', 'data']),
        ]),
        getDoc(doc(db, 'companies', companyId, 'companySettings', 'main')),
      ]);

      const cashierList = mainDocSnap.exists() && Array.isArray(mainDocSnap.data().cashiers)
        ? mainDocSnap.data().cashiers
        : [];
      const cashierCount = cashierList.length;

      setUsedQuotas({
        productTypes: counts[0] || 0,
        products: counts[1] || 0,
        discounts: counts[2] || 0,
        cashiers: cashierCount,
      });
    };

    fetchData();
  }, [user, sessionId]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}, ${date.toLocaleTimeString()}`;
  };

  const isUnlimited = (val) => val === Infinity || val === 'ꝏ' || val === 'Unlimited';
  const displayTotal = (val, suffix = '') => isUnlimited(val) ? 'Unlimited' : `${val}${suffix}`;
  const calculatePercent = (used, total) => isUnlimited(total) ? 0 : (used / total) * 100;

  const trialExpired = subscription?.trialEnds && new Date() > new Date(subscription.trialEnds);
  const billingExpired = subscription?.nextBillingDate && new Date() > new Date(subscription.nextBillingDate);
  const billingCancelled = subscription?.status === 'canceled';
  const paymentUnsettled = subscription?.status !== 'active';
  const needsAttentionStatuses = ['past_due', 'unpaid', 'canceled', 'incomplete', 'incomplete_expired'];

  const shouldShowModal = trialExpired || billingExpired || needsAttentionStatuses.includes(subscription?.status);

  const isTrialing = subscription?.status === 'trialing';
  const isCanceling = subscription?.cancelAtPeriodEnd;
  const hasActiveSubscription =
    ((subscription?.status === 'active' && !billingExpired) || (isTrialing && !trialExpired) || (isCanceling && !billingExpired));
  const planLabel = isTrialing ? 'Free Trial' : (subscription?.plan || 'CashMate');

  const currentPlan = subscription?.plan || 'CashMate';
  const currentQuota = quotas[currentPlan] || quotas['Free'];

  const handleOpenBillingDashboard = async () => {
    const functions = getFunctions(app);
    const createPortal = httpsCallable(functions, 'createCustomerPortalLink');
    try {
      const { data } = await createPortal({ companyId });
      window.location.href = data.url;
    } catch (err) {
      console.error('Failed to redirect to billing portal', err);
      alert('Unable to open billing dashboard. Please try again later.');
    }
  };

  const handleManageSubscription = async () => {
    setIsLoading(prev => ({ ...prev, manageSubscription: true }));
    try {
      if (subscription?.status !== 'active') {
        setShowSubscribeModal(true);
      } else {
        await handleOpenBillingDashboard();
      }
    } catch (err) {
      console.error("Failed to manage subscription:", err);
    } finally {
      setIsLoading(prev => ({ ...prev, manageSubscription: false }));
    }
  };
  
  const handleBillingDashboard = async () => {
    setIsLoading(prev => ({ ...prev, billingDashboard: true }));
    try {
      await handleOpenBillingDashboard();
    } catch (err) {
      console.error("Failed to open billing dashboard:", err);
    } finally {
      setIsLoading(prev => ({ ...prev, billingDashboard: false }));
    }
  };

  const [isLoading, setIsLoading] = useState({
    manageSubscription: false,
    billingDashboard: false
  });

  if (loadingPage) {
    return (
      <div className="w-full flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
        <span className="text-white">Loading billing information...</span>
      </div>
    );
  }
  return (
    <div className="w-full max-w-6xl mx-auto p-6 text-white">
      <h2 className="text-3xl font-bold mb-6">Billing & Subscription</h2>

      <div className="flex border-b border-gray-700 mb-6 space-x-6">
        {['subscription', 'invoices'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-2 font-medium capitalize transition ${
              activeTab === tab ? 'border-b-2 border-purple-500 text-purple-400' : 'text-gray-400 hover:text-white'
            }`}
          >
            {tab === 'invoices' ? 'Past Invoices' : tab}
          </button>
        ))}
      </div>

      {showSubscribeModal && (
        <SubscribeModal
        isOpen={showSubscribeModal}
        onClose={() => setShowSubscribeModal(false)}
        hasExpired={shouldShowModal}
        companyId={companyId}
      />
      )}

      {activeTab === 'subscription' && (
        <div className="space-y-6">
          <div className="overflow-auto">
            <table className="w-full text-sm bg-[#1f1f1f] rounded-xl overflow-hidden">
              <thead className="bg-[#2b2b2b] text-white text-left">
                <tr>
                  <th className="py-3 px-4">Plan</th>
                  <th className="py-3 px-4">Status</th>
                  {subscription?.status === 'canceling' || billingCancelled ? (
                    <th className="py-3 px-4">Active Until</th>
                  ) : (
                    <th className="py-3 px-4">Next Billing</th>
                  )}
                  <th className="py-3 px-4">Billing Cycle</th>
                  <th className="py-3 px-4">Account Created</th>
                </tr>
              </thead>
              <tbody>
                <tr className="text-gray-300 border-t border-gray-700">
                  <td className="py-3 px-4">
                    <div className="flex items-center">
                      <span className="font-medium">{planLabel}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
  {/* Trial Active */}
  {subscription?.trialEnds && !trialExpired && (
    <span className="text-xs bg-yellow-400 text-white px-2 py-0.5 rounded">
      Trial Active
    </span>
  )}

  {/* Trial Expired */}
  {trialExpired && !hasActiveSubscription && !billingCancelled && (
    <span className="text-xs bg-red-400 text-white px-2 py-0.5 rounded">
      Trial Expired
    </span>
  )}

  {/* Active Subscription */}
  {hasActiveSubscription && (
    <span className="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded">
      Active
    </span>
  )}

  {/* Canceling */}
  {subscription?.status === 'canceling' && (
    <span className="ml-2 text-xs bg-orange-400 text-white px-2 py-0.5 rounded">
      Canceling
    </span>
  )}

  {/* Fully Cancelled */}
  {billingCancelled && (
    <span className="ml-2 text-xs bg-gray-500 text-white px-2 py-0.5 rounded">
      Cancelled
    </span>
  )}

  {/* Past Due / Unpaid */}
  {(subscription?.status === 'past_due' || subscription?.status === 'unpaid') && (
    <span className="ml-2 text-xs bg-red-500 text-white px-2 py-0.5 rounded">
      Payment Due
    </span>
  )}

  {/* Payment Due */}
  {billingExpired && !billingCancelled && subscription?.status !== 'canceling' && (
    <span className="ml-2 text-xs bg-red-500 text-white px-2 py-0.5 rounded">
      Payment Due
    </span>
  )}
</td>
                  <td className="py-3 px-4">{formatDate(subscription.nextBillingDate)}</td>
                  <td className="py-3 px-4">{subscription?.billingCycle || 'Monthly'}</td>
                  <td className="py-3 px-4">{formatDate(createdAt)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold">Your Usage</h3>
            <div className="flex space-x-4">
              
            <button
  onClick={handleManageSubscription}
  disabled={isLoading.manageSubscription}
  className="bg-purple-600 hover:bg-purple-500 px-6 py-2 rounded-lg font-bold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[180px]"
>
  {isLoading.manageSubscription && (
    <svg
      className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  )}

  {isLoading.manageSubscription
    ? 'Loading...'
    : !hasActiveSubscription
    ? 'Subscribe Now'
    : 'Manage Subscription'}
</button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <QuotaCard
              title="Cashier Limit"
              used={usedQuotas.cashiers}
              total={displayTotal(currentQuota.cashier)}
              percent={calculatePercent(usedQuotas.cashiers, currentQuota.cashier)}
              isUnlimited={isUnlimited(currentQuota.cashier)}
              onUpgrade={() => handleOpenBillingDashboard()}
            />
            <QuotaCard
              title="Product Types"
              used={usedQuotas.productTypes}
              total={displayTotal(currentQuota.productType)}
              percent={calculatePercent(usedQuotas.productTypes, currentQuota.productType)}
              isUnlimited={isUnlimited(currentQuota.productType)}
              onUpgrade={() => handleOpenBillingDashboard()}
            />
            <QuotaCard
              title="Products"
              used={usedQuotas.products}
              total={displayTotal(currentQuota.product)}
              percent={isUnlimited(currentQuota.product) ? 0 : (usedQuotas.products / currentQuota.product) * 100}
              isUnlimited={isUnlimited(currentQuota.product)}
              onUpgrade={() => handleOpenBillingDashboard()}
            />
            <QuotaCard
              title="Discounts"
              used={usedQuotas.discounts}
              total={displayTotal(currentQuota.discount)}
              percent={calculatePercent(usedQuotas.discounts, currentQuota.discount)}
              isUnlimited={isUnlimited(currentQuota.discount)}
              onUpgrade={() => handleOpenBillingDashboard()}
            />
          </div>
        </div>
      )}

      {activeTab === 'invoices' && (
        <div className="space-y-4 text-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold">Billing History</h3>
            <div className="flex space-x-4">
              <button
  onClick={handleBillingDashboard}
  disabled={isLoading.billingDashboard}
  className="bg-purple-600 hover:bg-purple-500 px-6 py-2 rounded-lg font-bold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[180px]"
>
  {isLoading.billingDashboard && (
    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  )}
  {isLoading.billingDashboard ? 'Loading...' : 'Billing Dashboard'}
</button>
            </div>
          </div>
          {loadingInvoices ? (
            <p>Loading invoices...</p>
          ) : invoices.length === 0 ? (
            <p>No invoices found.</p>
          ) : (
            <table className="w-full bg-[#1f1f1f] rounded-lg overflow-hidden">
              <thead className="text-left bg-[#2b2b2b]">
                <tr>
                  <th className="py-3 px-4">Invoice ID</th>
                  <th className="py-3 px-4">Amount</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Link</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id} className="border-t border-gray-700">
                    <td className="py-2 px-4">{inv.id}</td>
                    <td className="py-2 px-4">{inv.amount.toFixed(2)} {inv.currency}</td>
                    <td className="py-2 px-4 capitalize">{inv.status}</td>
                    <td className="py-2 px-4">{new Date(inv.created).toLocaleString()}</td>
                    <td className="py-2 px-4">
                      <button
                        onClick={() => window.open(inv.hosted_invoice_url, '_blank')}
                        className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

    </div>
  );
}

function QuotaCard({ title, used, total, percent, onUpgrade, isUnlimited }) {
  const isFull = !isUnlimited && percent >= 100;

  return (
    <div className="bg-[#1f1f1f] rounded-xl p-4">
      <h4 className="font-semibold mb-1">{title}</h4>

      {!isUnlimited && (
        <div className="w-full h-2 bg-gray-700 rounded-full mb-1">
          <div
            className={`h-2 ${isFull ? 'bg-red-500' : 'bg-purple-500'} rounded-full`}
            style={{ width: `${Math.min(percent, 100)}%` }}
          />
        </div>
      )}

      <div className="text-xs text-gray-400 flex justify-between mb-1">
        <span>{used} used</span>
        <span>{isUnlimited ? 'Unlimited' : `${total} total`}</span>
      </div>

      {isFull && (
        <div className="text-xs text-red-500 font-medium">
          Limit reached.{' '}
          <button
            onClick={onUpgrade}
            className="underline hover:text-red-400"
          >
            Upgrade your plan
          </button>
        </div>
      )}
    </div>
  );
}


function Card({ title, used }) {
  return (
    <div className="bg-[#1f1f1f] rounded-xl p-4">
      <h4 className="font-semibold mb-1">{title}</h4>
      <div className="text-xs text-gray-400">{used}</div>
    </div>
  );
}

function SubscribeModal({
  isOpen,
  onClose,
  hasExpired,
  companyId,
}) {
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center">
      <div className="bg-[#1f1f1f] w-full max-w-lg p-6 rounded-xl text-white">
        {hasExpired && (
          <div className="bg-red-500 text-white text-sm px-4 py-2 rounded mb-4">
            Your subscription has expired or is inactive. Please renew your subscription to continue using all features.
          </div>
        )}
        <h2 className="text-xl font-bold mb-4 text-white">
          Choose a plan to subscribe
        </h2>

        <p className="text-sm text-gray-300 mb-6">
          RM 130 / month • Unlimited access • No quotas
        </p>

        <div className="flex justify-end gap-4">
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded-lg text-sm"
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              setIsLoadingCheckout(true);
              await redirectToCheckout({
                companyId,
              });
              setIsLoadingCheckout(false);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-bold ${
              isLoadingCheckout ? 'bg-gray-500' : 'bg-purple-600 hover:bg-purple-700'
            }`}
            disabled={isLoadingCheckout}
          >
            {isLoadingCheckout ? 'Redirecting...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
